import { useMutation, useQuery } from '@apollo/client'
import { uuid4 } from '@sentry/utils'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useHistory } from 'react-router'
import { Link } from 'react-router-dom'

import { Button, Card, CryptoIcon, Input, Spinner } from '../../components'
import { EtherscanAddress } from '../../components/etherscan-address'
import { LoanQualification } from '../../components/loan-qualification'
import { PoolsTable } from '../../components/pools-table'
import { AddAccountPaths, Paths } from '../../config/routes'
import { useUser } from '../../contexts/user/user-context'
import { STATUS } from '../../lib/constants'
import { Notify } from '../../lib/helpers'
import {
    CreateLoanApplication,
    GetLoan,
    GetPreQualificationData,
    UpdateLoanApplication,
} from './queries'

export function Loans() {
    const { userProfile } = useUser()
    const history = useHistory()

    const { data, loading } = useQuery(GetPreQualificationData, {
        fetchPolicy: 'network-only',
    })

    const user = useMemo(() => {
        //@ts-ignore
        const profile = userProfile?.getUserProfile
        if (!profile) return null
        return profile
    }, [userProfile])

    const loanApplicationId = useMemo(() => {
        //@ts-ignore
        const profile = userProfile?.getUserProfile
        if (!profile) return null
        return profile.loanApplicationId
    }, [userProfile])

    const { data: loan } = useQuery(GetLoan, {
        variables: { id: loanApplicationId },
        fetchPolicy: 'network-only',
    })

    useEffect(() => {
        if (user && loanApplicationId) {
            if (
                loan &&
                loan.getOneLoanApplicationFromNode.data.status === STATUS.DONE
            ) {
                history.push(Paths.LOANS_HISTORY)
            } else {
                history.push('/app/loan/' + loanApplicationId)
            }
        }
    }, [user, history, loan, loanApplicationId])

    const [loanAmount, setLoanAmount] = useState('')
    const [purpose, setPurpose] = useState('')

    const dataClasses = 'not-italic font-light text-sm leading-3 py-4'
    const enfasisClasses = ' text-black '
    const [createLoanApplication] = useMutation(CreateLoanApplication)
    const [updateLoanApplicationId] = useMutation(UpdateLoanApplication)
    const createLoan = useCallback(async () => {
        //@ts-ignore
        if (isNaN(loanAmount)) {
            Notify(
                'danger',
                'Whoops',
                'Try to enter a valid number for your loan',
            )
            return
        }
        if (user) {
            const dateKey = new Date().toISOString().substring(0, 10)
            const result = await createLoanApplication({
                variables: {
                    client: {
                        client_id: '',
                        user_id: user.id,
                    },
                    loan_application: {
                        data: {
                            // Todo: define how we populate this data
                            amount: parseInt(loanAmount),
                            date: dateKey,
                            fees: 0,
                            interest: 0,
                            interest_rate: 0.15,
                            iso_currency_code: 'USD',
                            last_updated: dateKey,
                            ltv_ratio: 0.4,
                            prequalification_amount: 1234,
                            principal: 12345,
                            term: 3,
                        },
                        institution_id: '1',
                        loan_application_id: uuid4(),
                        loan_product: 'Uncollateralized USDC Loan',
                        name: user.id,
                        status: 0,
                        loan_purpose: purpose,
                    },
                },
            })
            console.log('result => ', result)
            if (
                result.data.createLoanApplicationInNode.data.loan_application_id
            ) {
                await updateLoanApplicationId({
                    variables: {
                        input: {
                            loanApplicationId:
                                result.data.createLoanApplicationInNode.data
                                    .loan_application_id,
                        },
                    },
                })
                Notify(
                    'success',
                    'Yippee!',
                    'Your loan application has been submited',
                )
            }
        }
    }, [
        loanAmount,
        purpose,
        user,
        createLoanApplication,
        updateLoanApplicationId,
    ])
    if (loading) return <Spinner />

    const {
        debtToIncomeRatio,
        freeCashFlow,
        monthlyIncome,
        totalCryptoWorth,
        totalDebt,
        totalNetWorth,
        preQualified,
        totalAssets,
    } = data.getPreQualificationData
    return (
        <>
            <div className='grid grid-cols-6 gap-16 pt-8'>
                <div className='col-span-6 xl:col-span-2'>
                    <Card className='w-full h-full py-8'>
                        <LoanQualification
                            debtToIncomeRatio={debtToIncomeRatio}
                            freeCashFlow={freeCashFlow}
                            monthlyIncome={monthlyIncome}
                            totalCryptoWorth={totalCryptoWorth}
                            totalDebt={totalDebt}
                            totalNetWorth={totalNetWorth}
                            totalAssets={totalAssets}
                        />
                    </Card>
                </div>
                {!preQualified ? (
                    <div className='col-span-6 xl:col-span-4'>
                        <Card className='w-full h-full py-8'>
                            <h1>Connect Accounts</h1>
                            <p className='w-4/5 my-8 text-black'>
                                You can increase your DeFi credit score by
                                connecting more bank and crypto accounts. We use
                                this data to pre-qualify for an uncollateralized
                                loan.{' '}
                                <b>
                                    The more accounts you connect the more
                                    accurate your credit score
                                </b>
                            </p>
                            <Link to={AddAccountPaths.ADD_BANK_ACCOUNT}>
                                <Button className={'py-4 px-8'}>
                                    + Connect Accounts
                                </Button>
                            </Link>
                        </Card>
                    </div>
                ) : (
                    <div className='col-span-6 xl:col-span-4'>
                        <Card className='w-full h-full py-8 '>
                            <h3>🌽 Masa USDC A Pool</h3>
                            <PoolsTable
                                headers={[
                                    'Asset',
                                    'Masa NFT USDC',
                                    'Pool Balance',
                                    'Pool Utilization',
                                ]}
                                items={
                                    <>
                                        <tr
                                            className='border-b border-solid border-gray border-opacity-10 group'
                                            data-test-id={`transaction-item`}>
                                            <td
                                                className={`${dataClasses} ${enfasisClasses}`}>
                                                <div className='flex flex-row justify-center w-full p-2'>
                                                    <CryptoIcon name='USDC' />
                                                </div>
                                            </td>
                                            <td
                                                className={`${dataClasses} ${enfasisClasses}`}>
                                                <div className='flex flex-row justify-center font-semibold truncate text-linkYellow'>
                                                    <EtherscanAddress
                                                        address={
                                                            '0x7899...67h7'
                                                        }
                                                    />
                                                </div>
                                            </td>
                                            <td
                                                className={`${dataClasses} ${enfasisClasses}`}>
                                                <div className='flex flex-col items-center overflow-visible text-left break-words truncate'>
                                                    <p className='font-normal text-black'>
                                                        $134,608.00
                                                    </p>
                                                </div>
                                            </td>
                                            <td
                                                className={`${dataClasses} ${enfasisClasses}`}>
                                                <div className='flex flex-col items-center overflow-visible text-left break-words truncate'>
                                                    <p>
                                                        <span className='font-normal text-black'>
                                                            $50,000.00
                                                        </span>{' '}
                                                        borrowed
                                                    </p>
                                                    <p>
                                                        <span className='font-normal text-black'>
                                                            $84,508.00
                                                        </span>{' '}
                                                        available
                                                    </p>
                                                </div>
                                            </td>
                                        </tr>
                                    </>
                                }
                            />

                            <div className='mt-4 border-b border-gray2'>
                                <div className='grid w-full grid-cols-2 mb-4 border-b border-gray2'>
                                    <div className='flex flex-col justify-center col-span-1 p-4'>
                                        <h3>Your loan</h3>
                                        <div className='flex flex-row w-full'>
                                            <p className='text-lg text-black'>
                                                Maximum loan amount: $2,500.00
                                            </p>
                                        </div>
                                    </div>
                                    <div className='col-span-1 p-4'>
                                        <p className='text-md'>
                                            Loan term:{' '}
                                            <span className='text-black'>
                                                6 months
                                            </span>
                                        </p>
                                        <p className='text-md'>
                                            Interest rate:{' '}
                                            <span className='text-black'>
                                                15% APY
                                            </span>
                                        </p>
                                        <p className='text-md'>
                                            Collateral:{' '}
                                            <span className='text-black'>
                                                None required
                                            </span>
                                        </p>
                                    </div>
                                </div>
                                <div className='py-8 mb-8 border-b border-gray2'>
                                    <p className='text-md'>Purpose of loan*</p>
                                    <Input
                                        value={purpose}
                                        onChange={e =>
                                            setPurpose(e.target.value)
                                        }
                                        type={'textarea'}
                                        placeholder='Write a description of what the loan will be used for in this space. The is no character limit for this information and it is stored to the backend.'
                                    />
                                </div>
                                <div className='grid grid-cols-2 grid-rows-1 gap-8'>
                                    <div className='flex flex-col items-center'>
                                        <div className='flex flex-row justify-between w-full mb-4 text-sm font-light '>
                                            <p className='text-gray8'>
                                                Loan amount
                                            </p>
                                            <p className='text-gray8'>
                                                Wallet balance: $500 USDC
                                            </p>
                                        </div>
                                        <div className='relative flex flex-row items-center w-full'>
                                            <Input
                                                placeholder='0.00'
                                                value={loanAmount}
                                                onChange={e => {
                                                    setLoanAmount(
                                                        e.target.value,
                                                    )
                                                }}
                                            />
                                            <div className='absolute flex flex-row gap-x-4 right-6'>
                                                <p className='mb-0 '>USDC</p>
                                                <p className='mb-0 text-black'>
                                                    Max
                                                </p>
                                            </div>
                                        </div>
                                        <Button
                                            className='w-full py-4 mt-4'
                                            onClick={e => {
                                                createLoan()
                                            }}
                                            disabled={!loanAmount || !purpose}>
                                            Request loan
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        </Card>
                    </div>
                )}
            </div>
        </>
    )
}
